import type * as Item from "../../lib/models/item.server.ts"

import * as UI from "@iyk/ui"
import * as React from "react"

import useEmblaCarousel from "embla-carousel-react"

import { each } from "@iyk/flow"
import { useMediaQuery } from "../../lib/hooks/use-media-query.ts"

export const ItemMediaGallery = ({ mediaGallery }: { mediaGallery: Item.MediaGallery }) => {
  const isDesktop = useMediaQuery("(width >= 768px)")
  const [showTopShadow, setShowTopShadow] = React.useState(false)
  const [showBottomShadow, setShowBottomShadow] = React.useState(false)

  const [carouselRef, carouselAPI] = useEmblaCarousel({
    loop: false,
  })

  const [thumbRef, thumbAPI] = useEmblaCarousel({
    loop: false,
    containScroll: "keepSnaps",
    dragFree: true,
    axis: isDesktop ? "y" : "x",
  })

  const [selectedIndex, setSelectedIndex] = React.useState(0)

  const onThumbClick = React.useCallback(
    (index: number) => {
      if (!carouselAPI || !thumbAPI) return
      carouselAPI.scrollTo(index)
    },
    [carouselAPI, thumbAPI],
  )

  const onSelect = React.useCallback(() => {
    if (!carouselAPI || !thumbAPI) return
    const selectedIndex = carouselAPI.selectedScrollSnap()
    setSelectedIndex(selectedIndex)
    thumbAPI.scrollTo(selectedIndex)
  }, [carouselAPI, thumbAPI])

  const updateShadows = React.useCallback(() => {
    if (!thumbAPI) return
    const { scrollProgress, scrollSnapList, selectedScrollSnap } = thumbAPI
    setShowTopShadow(scrollProgress() > 0)
    setShowBottomShadow(scrollProgress() < 1 && selectedScrollSnap() < scrollSnapList().length - 1)
  }, [thumbAPI])

  React.useEffect(() => {
    if (!carouselAPI || !thumbAPI) return

    onSelect()
    updateShadows()

    carouselAPI.on("select", onSelect).on("reInit", onSelect)
    thumbAPI.on("scroll", updateShadows).on("reInit", updateShadows)

    return () => {
      carouselAPI.off("select", onSelect).off("reInit", onSelect)
    }
  }, [carouselAPI, thumbAPI, onSelect])

  return (
    <>
      <span className="md:col-span-6 flex aspect-square relative mb-6 md:mb-0">
        <span className="flex grow overflow-hidden" ref={carouselRef}>
          <span className="flex grow flex-row md:flex-row [touch-action:pan-x_pan-y_pinch-zoom]">
            {each(mediaGallery, (media) => (
              <UI.Media
                key={media.src}
                src={media.src}
                className="size-full aspect-square bg-gray-2"
                loading="lazy"
                camera-controls
              />
            ))}
          </span>
        </span>
        {mediaGallery.length === 1 && !isDesktop ? null : (
          <span
            className={classForThumbListContainer({
              topShadow: showTopShadow,
              bottomShadow: showBottomShadow,
            })}
            ref={thumbRef}
          >
            <span className="flex gap-1 grow w-full h-4 md:block md:size-full select-none">
              {each(mediaGallery, (media, index) => (
                <button
                  key={media.src}
                  type="button"
                  className={classForThumb({
                    selected: selectedIndex === index,
                  })}
                  onClick={() => onThumbClick(index)}
                >
                  <span className={UI.classForStack()}>
                    <UI.Media
                      key={media.src}
                      src={media.src}
                      className={UI.classForLayer({
                        className:
                          "hidden md:block size-full aspect-square object-contain bg-gray-2",
                      })}
                      loading="lazy"
                    />
                    <span
                      className={UI.classForLayer({
                        className:
                          selectedIndex === index
                            ? "[box-shadow:black_0_0_0_1px_inset,white_0_0_0_3px_inset]"
                            : "hover:[box-shadow:black_0_0_0_1px_inset]",
                      })}
                    />
                  </span>
                </button>
              ))}
            </span>
          </span>
        )}
      </span>
    </>
  )
}

const classForThumbListContainer = UI.cva(
  [
    "overflow-hidden absolute -bottom-4 md:-top-0 w-full h-4 md:w-14 md:h-full md:-ml-14",
    "before:transition-opacity after:transition-opacity duration-500",
  ],
  {
    variants: {
      topShadow: {
        true: "md:before:absolute md:before:opacity-100 md:before:left-0 md:before:top-0 md:before:h-20 md:before:w-full md:before:bg-gradient-to-b md:before:from-white md:before:to-transparent md:before:z-10",
        false: "before:opacity-0",
      },
      bottomShadow: {
        true: "md:after:absolute md:after:opacity-100 md:after:left-0 md:after:bottom-0 md:after:h-20 md:after:w-full md:after:bg-gradient-to-t md:after:from-white md:after:to-transparent md:after:z-10",
        false: "after:opacity-0",
      },
    },
  },
)

const classForThumb = UI.cva(
  [
    "block min-h-1 grow bg-no-repeat bg-center [background-size:100%_1px] md:[background-image:none]",
  ],
  {
    variants: {
      selected: {
        true: "[background-image:linear-gradient(var(--foreground),var(--foreground))]",
        false: "[background-image:linear-gradient(var(--gray-8),var(--gray-8))]",
      },
    },
  },
)
